import { ErrorCodes } from '@aline/common';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { getErrorCode } from 'src/store/errors';

/** The codes that mean "we can't show you this", whichever way it was raised. */
const NOT_FOUND_ERROR_CODES: ErrorCodes[] = [ErrorCodes.NOT_FOUND, ErrorCodes.UNAUTHORIZED];

interface GetIsNotFoundOptions {
  /** The resolved entity field, e.g. `data?.agentThread` or `data?.workflow`. */
  entity: object | null | undefined;
  /** The query hook's `error`. */
  error: unknown;
  loading: boolean;
}

/**
 * Whether a query hook's `error` carries any of `codes`. Shared so the surfaces
 * that branch on a single code — the editor, which sends `UNAUTHORIZED` to its
 * request-access flow — unwrap `CombinedGraphQLErrors` the same way this does.
 */
export function getHasErrorCode(error: unknown, codes: ErrorCodes[]): boolean {
  if (!CombinedGraphQLErrors.is(error)) return false;

  return error.errors.some(graphQLError => {
    const code = getErrorCode(graphQLError);
    return Boolean(code && codes.includes(code));
  });
}

/**
 * Normalises the two ways this app reports "we can't show you this" onto one
 * answer.
 *
 * Chats and documents raise a GraphQL error carrying a code. Workflows do not:
 * `workflow(workflowId)` is nullable and its resolver returns `null` both for a
 * row that does not exist and for one the requester cannot see, so no error ever
 * reaches the client. Anything keyed only off `error` silently misses them.
 *
 * Missing and inaccessible deliberately collapse to the same answer, so the
 * response never confirms whether a record exists. Surfaces that must tell them
 * apart — the editor, which redirects to its request-access flow on
 * `UNAUTHORIZED` — branch on the code themselves instead of calling this.
 */
export function getIsNotFound({ entity, error, loading }: GetIsNotFoundOptions): boolean {
  // A request in flight is not a missing record; never flash the state mid-load.
  if (loading) return false;

  /**
   * Checked before `error`, because a NOT_FOUND or UNAUTHORIZED raised by a
   * nullable *nested* field arrives alongside a fully resolved entity. The page
   * the user asked for is there; only part of it is missing, and blanking the
   * whole route over that hides content they can see.
   */
  if (entity) return false;

  // Network and other uncoded failures stay with the global handler in apollo-client.
  if (error) return getHasErrorCode(error, NOT_FOUND_ERROR_CODES);

  return true;
}
